import { useEditorStore } from '@/stores/editor/editor-store';
import { useResponsiveStore } from '@/stores/editor/responsive-store';
import { useEngagementStore } from '@/stores/service-provider/engagement-store';
import {
  PHASE_11_DATA_BINDING_INSTRUCTIONS,
  findRelatedBindingComponents,
  summarizeBindingContextForAi,
} from '@/services/ai/BindingContextSummary';
import type { EditorContext } from '@/services/api/agentApi';
import type { AiBindingContextComponentInput } from '@/services/ai/BindingContextSummary';

const MAX_COMPONENTS = 50;
const MAX_CONTEXT_CHARS = 12000;
const FORBIDDEN_OBJECT_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const SENSITIVE_PROPERTY_PATTERN = /token|password|secret|authorization|header|credential/i;
const RAW_DATA_PROPERTY_KEYS = new Set([
  'data',
  'datasource',
  'sampledata',
  'rawdata',
  'rawdataset',
  'dataset',
  'datasets',
  'rows',
  'rawrows',
  'records',
  'resultrows',
  'runtimerows',
  'saferuntimerows',
  'aisaferuntimerows',
  'aicontextsaferuntimerows',
  'blob',
]);
const EXPLICIT_SAFE_RUNTIME_ROW_KEYS = [
  'safeRuntimeRows',
  'aiSafeRuntimeRows',
  'aiContextSafeRuntimeRows',
];
const DATA_CONTEXT_COMPONENT_TYPES = new Set([
  'data-text',
  'chart',
  'bar-chart',
  'line-chart',
  'pie-chart',
  'scatter-chart',
  'area-chart',
  'heatmap-chart',
  'treemap-chart',
  'gauge-chart',
  'vertical-gauge-chart',
  'data-card',
  'radial-dial',
  'level-indicator',
  'map-chart',
  'table',
  'advanced-table',
  'list',
  'kanban-board',
  'checklist',
  'horizontal-timeline',
  'assignment-widget',
  'gantt-chart',
  'calendar',
  'project-hub',
  'questionnaire',
  'voting-card',
  'time-tracker',
]);

/** Properties that are relevant for agent decision-making */
const KEEP_PROPS = ['content', 'text', 'chartType', 'label', 'src', 'placeholder', 'columns'];

/** Visual properties included for all components so agents can match sibling styling */
const VISUAL_KEEP_PROPS = [
  'backgroundColor', 'borderRadius', 'padding', 'border', 'color',
  'fontSize', 'fontWeight', 'gap', 'direction', 'alignItems',
  'justifyContent', 'iconName', 'iconSize',
  'theme', 'activeColor', 'inactiveColor',
];

/** Infer a human-readable semantic label for a component */
function inferLabel(c: { type: string; properties: Record<string, unknown>; children?: string[]; style?: Record<string, unknown> }): string {
  const type = (c.type || '').toUpperCase();
  const props = c.properties || {};
  const style = c.style || {};

  switch (type) {
    case 'TEXT': {
      const text = String(props.content || props.text || '').slice(0, 30);
      const fontSize = Number(props.fontSize || style.fontSize || 16);
      const prefix = fontSize >= 24 ? 'Heading' : 'Text';
      return text ? `${prefix}: "${text}"` : prefix;
    }
    case 'CHART': {
      const chartType = String(props.chartType || 'chart');
      return `Chart (${chartType})`;
    }
    case 'TABLE':
      return 'Table';
    case 'DATA_TEXT': {
      const label = String(props.label || props.text || 'value');
      return `KPI: ${label.slice(0, 20)}`;
    }
    case 'FLEX_CONTAINER': {
      const dir = String(props.flexDirection || style.flexDirection || 'column');
      const childCount = c.children?.length || 0;
      return `Container (${dir}, ${childCount} children)`;
    }
    case 'BUTTON': {
      const text = String(props.text || props.content || props.label || 'button').slice(0, 20);
      return `Button: "${text}"`;
    }
    case 'INPUT': {
      const hint = String(props.placeholder || props.label || 'input').slice(0, 20);
      return `Input: ${hint}`;
    }
    case 'IMAGE':
      return 'Image';
    default:
      return type || 'Component';
  }
}

/** Count parent chain depth for a component */
function computeHierarchyDepth(
  componentId: string,
  parentMap: Map<string, string | undefined>,
): number {
  let depth = 0;
  let currentId: string | undefined = componentId;
  while (currentId && parentMap.has(currentId)) {
    const parentId = parentMap.get(currentId);
    if (!parentId) break;
    depth++;
    currentId = parentId;
  }
  return depth;
}

export interface SerializeOptions {
  /** Component IDs that should include full properties and styles, bypassing KEEP_PROPS filter and style trimming */
  detailedComponentIds?: string[];
  /** Include breakpoint style overrides for every serialized component. Intended for direct style operations. */
  includeBreakpointStyles?: boolean;
}

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;

  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!/^-?\d+(?:\.\d+)?(?:px)?$/.test(trimmed)) return undefined;

  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isSafeObjectKey(key: string): boolean {
  return !FORBIDDEN_OBJECT_KEYS.has(key);
}

function isRawOrSensitiveContextKey(key: string): boolean {
  return RAW_DATA_PROPERTY_KEYS.has(key.toLowerCase()) || SENSITIVE_PROPERTY_PATTERN.test(key);
}

function sanitizeDetailedPropertyValue(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    const sanitizedItems: unknown[] = [];
    for (const item of value) {
      const sanitizedItem = sanitizeDetailedPropertyValue(item);
      if (sanitizedItem !== undefined) sanitizedItems.push(sanitizedItem);
    }
    return sanitizedItems;
  }

  if (!isRecord(value)) {
    return String(value);
  }

  const sanitizedObject: Record<string, unknown> = {};
  copySafeProperties(sanitizedObject, value);
  return sanitizedObject;
}

function copySafeProperties(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): void {
  for (const [key, value] of Object.entries(source)) {
    if (!isSafeObjectKey(key) || isRawOrSensitiveContextKey(key)) {
      continue;
    }

    const sanitizedValue = sanitizeDetailedPropertyValue(value);
    if (sanitizedValue !== undefined) {
      target[key] = sanitizedValue;
    }
  }
}

function findExplicitSafeRuntimeRows(component: {
  properties: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}): unknown {
  for (const key of EXPLICIT_SAFE_RUNTIME_ROW_KEYS) {
    const metadataRows = component.metadata?.[key];
    if (metadataRows !== undefined) return metadataRows;

    const propertyRows = component.properties[key];
    if (propertyRows !== undefined) return propertyRows;
  }

  return undefined;
}

function collectDataSourceRefs(
  dataSource: unknown,
  refs: Set<string>,
): boolean {
  if (!isRecord(dataSource)) return false;

  const queryDefinition = isRecord(dataSource.queryDefinition) ? dataSource.queryDefinition : undefined;
  if (typeof queryDefinition?.schemaName === 'string') refs.add(queryDefinition.schemaName);
  if (typeof queryDefinition?.schemaId === 'string') refs.add(queryDefinition.schemaId);

  const composite = isRecord(dataSource.compositeQueryDefinition) ? dataSource.compositeQueryDefinition : undefined;
  const sources = Array.isArray(composite?.sources) ? composite.sources : [];
  for (const source of sources) {
    if (!isRecord(source)) continue;
    const sourceRecord = source;
    if (typeof sourceRecord.schemaName === 'string') refs.add(sourceRecord.schemaName);
    if (typeof sourceRecord.schemaId === 'string') refs.add(sourceRecord.schemaId);
  }

  return true;
}

/**
 * Compact serialization of editor state for agent payloads.
 * Includes semantic labels, hierarchy depth, and data binding summary.
 * Limits to 50 components. If context exceeds 4000 chars, progressively trims.
 */
export function serialize(options?: SerializeOptions): EditorContext {
  const editorState = useEditorStore.getState();
  const responsiveState = useResponsiveStore.getState();
  const dimensions = responsiveState.getViewportDimensions(responsiveState.activeViewport);
  const canvasWidth = toFiniteNumber(dimensions?.width) ?? 1024;
  const canvasHeight = toFiniteNumber(dimensions?.height) ?? 768;
  const breakpoint: 'mobile' | 'tablet' | 'desktop' =
    canvasWidth <= 599 ? 'mobile' : canvasWidth <= 1023 ? 'tablet' : 'desktop';

  // Build parent map for depth calculation
  const parentMap = new Map<string, string | undefined>();
  for (const c of editorState.components) {
    parentMap.set(c.id, c.parentId);
  }

  // Count data-bound components and referenced schemas
  let boundCount = 0;
  const referencedSchemas = new Set<string>();

  // True BFS ordering using children arrays so siblings appear in their correct visual order.
  // A plain depth-sort leaves sibling ordering dependent on store order (which can diverge
  // from the children array order), causing the LLM to misidentify "top/second/third row".
  const idToComponent = new Map(editorState.components.map((c) => [c.id, c]));
  const bfsOrdered: typeof editorState.components = [];
  const bfsQueue = editorState.components.filter((c) => !c.parentId);
  while (bfsQueue.length > 0) {
    const current = bfsQueue.shift();
    if (!current) continue;
    bfsOrdered.push(current);
    const childIds = Array.isArray(current.children) ? current.children : [];
    for (const childId of childIds) {
      const child = idToComponent.get(childId);
      if (child) bfsQueue.push(child);
    }
  }
  // Append components unreachable from roots (defensive)
  const bfsIds = new Set(bfsOrdered.map((c) => c.id));
  for (const c of editorState.components) {
    if (!bfsIds.has(c.id)) bfsOrdered.push(c);
  }
  const allComponents = bfsOrdered.slice(0, MAX_COMPONENTS);

  const detailedIds = new Set(options?.detailedComponentIds || []);
  type EditorComponent = (typeof editorState.components)[number];
  type SerializedComponent = EditorContext['components'][number];

  const toBindingContextInput = (component: EditorComponent): AiBindingContextComponentInput => {
    const safeRuntimeRows = findExplicitSafeRuntimeRows(component);

    return {
      id: component.id,
      type: component.type,
      name: component.name,
      properties: component.properties,
      metadata: component.metadata,
      ...(safeRuntimeRows !== undefined && { safeRuntimeRows }),
    };
  };

  const bindingContextInputs = allComponents.map(toBindingContextInput);
  const bindingContextInputById = new Map(
    bindingContextInputs.map((component) => [component.id, component]),
  );

  const shouldIncludeBindingContext = (component: EditorComponent): boolean => (
    component.properties.dataBinding !== undefined ||
    findExplicitSafeRuntimeRows(component) !== undefined ||
    DATA_CONTEXT_COMPONENT_TYPES.has(component.type)
  );

  const serializeBindingContext = (
    component: EditorComponent,
  ): SerializedComponent['bindingContext'] | undefined => {
    if (!shouldIncludeBindingContext(component)) {
      return undefined;
    }

    const bindingInput = bindingContextInputById.get(component.id);
    if (bindingInput === undefined) {
      return undefined;
    }

    const relatedComponents = findRelatedBindingComponents(
      bindingInput,
      bindingContextInputs,
    );

    return summarizeBindingContextForAi(bindingInput, {
      relatedComponents: relatedComponents.length > 0 ? bindingContextInputs : [],
      safeRuntimeRows: findExplicitSafeRuntimeRows(component),
    });
  };

  const serializeSize = (size: EditorComponent['size']): SerializedComponent['size'] => {
    const width = toFiniteNumber(size?.width);
    const height = toFiniteNumber(size?.height);

    return width !== undefined && height !== undefined ? { width, height } : undefined;
  };

  const mapComponent = (c: EditorComponent, trimStyles: boolean): SerializedComponent => {
    const isDetailed = detailedIds.has(c.id);
    const size = serializeSize(c.size);
    const bindingContext = serializeBindingContext(c);

    // Detailed components: all properties. Normal: KEEP_PROPS + VISUAL_KEEP_PROPS filter.
    const relevantProps: Record<string, unknown> = {};
    if (isDetailed) {
      copySafeProperties(relevantProps, c.properties || {});
    } else {
      for (const key of KEEP_PROPS) {
        if (!isRawOrSensitiveContextKey(key) && c.properties[key] !== undefined) {
          relevantProps[key] = c.properties[key];
        }
      }
      // Include visual properties so agents can match sibling styling
      for (const key of VISUAL_KEEP_PROPS) {
        if (c.properties[key] !== undefined) relevantProps[key] = c.properties[key];
      }
    }

    // Track data bindings
    if (collectDataSourceRefs(c.properties.dataSource, referencedSchemas)) {
      boundCount++;
    }

    // Style: detailed components always include style; non-detailed respect trimStyles
    const includeStyle = isDetailed ? true : !trimStyles;

    // Breakpoint styles: detailed components by default, all components for direct style operations.
    let breakpointStyles: Record<string, unknown> | undefined;
    if ((isDetailed || options?.includeBreakpointStyles === true) && c.responsivePosition?.breakpoints) {
      const safeBreakpointStyles: Record<string, unknown> = {};
      for (const [key, bp] of Object.entries(c.responsivePosition.breakpoints)) {
        if (isSafeObjectKey(key) && bp?.style && Object.keys(bp.style).length > 0) {
          safeBreakpointStyles[key] = bp.style;
        }
      }
      if (Object.keys(safeBreakpointStyles).length > 0) {
        breakpointStyles = safeBreakpointStyles;
      }
    }

    return {
      id: c.id,
      type: c.type,
      name: c.name || c.type,
      semanticLabel: inferLabel(c),
      hierarchyDepth: computeHierarchyDepth(c.id, parentMap),
      parentId: c.parentId,
      children: c.children,
      ...(Object.keys(relevantProps).length > 0 && { properties: relevantProps }),
      ...(includeStyle && Object.keys(c.style || {}).length > 0 && { style: c.style }),
      ...(breakpointStyles && { breakpointStyles }),
      ...(size && { size }),
      ...(bindingContext && { bindingContext }),
    };
  };

  // Keep a list of non-detailed components for progressive trimming
  const normalComponents = allComponents.filter(c => !detailedIds.has(c.id));

  // First pass: full serialization
  let components = allComponents.map(c => mapComponent(c, false));

  // Check size and progressively trim if needed
  let serialized = JSON.stringify(components);
  if (serialized.length > MAX_CONTEXT_CHARS) {
    // Trim 1: strip styles from non-detailed components only
    components = allComponents.map(c => mapComponent(c, detailedIds.has(c.id) ? false : true));
    serialized = JSON.stringify(components);
  }
  if (serialized.length > MAX_CONTEXT_CHARS) {
    // Trim 2: reduce non-detailed component count; always keep detailed + interactive controls
    const INTERACTIVE_TYPES = new Set([
      'radial-dial', 'toggle-switch', 'icon-button', 'push-button',
      'gauge-chart', 'vertical-gauge-chart', 'slider', 'stepper',
      'level-indicator', 'segmented-control', 'color-picker',
    ]);
    const interactiveIds = new Set(
      normalComponents.filter(c => INTERACTIVE_TYPES.has(c.type)).map(c => c.id)
    );
    const nonInteractiveNormals = normalComponents.filter(c => !INTERACTIVE_TYPES.has(c.type));
    const reducedCount = Math.max(
      0,
      Math.max(20, Math.floor(normalComponents.length * 0.6)) - interactiveIds.size,
    );
    const keepNormalIds = new Set([
      ...interactiveIds,
      ...nonInteractiveNormals.slice(0, reducedCount).map(c => c.id),
    ]);
    components = allComponents
      .filter(c => detailedIds.has(c.id) || keepNormalIds.has(c.id))
      .map(c => mapComponent(c, detailedIds.has(c.id) ? false : true));
  }

  const dataBindingSummary = boundCount > 0
    ? { boundCount, referencedSchemas: Array.from(referencedSchemas) }
    : undefined;

  // Multi-page summary
  const allPages = editorState.pages || [];
  const totalPages = allPages.length;
  const getComponentsForPage = (pageId: string): EditorComponent[] => {
    if (pageId === editorState.currentPageId) return editorState.components;
    return editorState.pageComponents?.get(pageId) || [];
  };

  const projectReferencedSchemas = new Set<string>();
  let projectBoundCount = 0;
  const pagesSummary = totalPages > 1
    ? allPages.map(p => {
        const pageComponents = getComponentsForPage(p.id);
        const typeCounts = new Map<string, number>();
        const pageRefs = new Set<string>();
        let pageBoundCount = 0;

        for (const component of pageComponents) {
          typeCounts.set(component.type, (typeCounts.get(component.type) || 0) + 1);
          if (collectDataSourceRefs(component.properties?.dataSource, pageRefs)) pageBoundCount++;
        }

        for (const ref of pageRefs) projectReferencedSchemas.add(ref);
        projectBoundCount += pageBoundCount;

        const topTypes = Array.from(typeCounts.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([type, count]) => ({ type, count }));

        const sampleComponents = pageComponents
          .filter(component => ['text', 'data-text', 'chart', 'table', 'button', 'flex-container'].includes(component.type))
          .slice(0, 8)
          .map(component => ({
            id: component.id,
            type: component.type,
            name: component.name || component.type,
            semanticLabel: inferLabel(component),
            hasDataSource: !!component.properties?.dataSource,
          }));

        return {
          id: p.id,
          name: p.name,
          isCurrent: p.id === editorState.currentPageId,
          componentCount: pageComponents.length,
          dataBoundCount: pageBoundCount,
          referencedSchemas: Array.from(pageRefs).slice(0, 6),
          topTypes,
          sampleComponents,
        };
      })
    : undefined;
  const projectDataBindingSummary = projectBoundCount > 0
    ? {
        boundCount: projectBoundCount,
        referencedSchemas: Array.from(projectReferencedSchemas).slice(0, 10),
      }
    : undefined;

  // Include specialization scope if service provider has active engagement
  const engagementTradeScope = useEngagementStore.getState().specializationScope;

  return {
    projectId: editorState.project?.id,
    schemaVersion: editorState.project?.schemaVersion === 2 ? 2 : 1,
    contextBuiltAtStep: 0,
    pageId: editorState.currentPageId || '',
    breakpoint,
    ...(editorState.activeColorScheme && { colorScheme: editorState.activeColorScheme }),
    ...(editorState.styleWriteTarget && { styleWriteTarget: editorState.styleWriteTarget }),
    components,
    selectedComponentIds: editorState.selectedComponents,
    canvasWidth,
    canvasHeight,
    dataBindingInstructions: PHASE_11_DATA_BINDING_INSTRUCTIONS,
    dataBindingSummary,
    projectDataBindingSummary,
    ...(totalPages > 1 && { totalPages, pages: pagesSummary }),
    ...(engagementTradeScope && { tradeScope: engagementTradeScope }),
  };
}

export const EditorContextSerializer = { serialize };
